import { useMemo } from 'react';
import { Spinner } from '@monksflow/monks-ui';
import { useNavigate, useParams } from 'react-router';

import { PersonaCard } from '@/entities/persona';
import { ROUTES } from '@/shared/config/routes';
import { AnalysisMarkdown, AnalysisSubHeader } from '@/pages/AnalysisResult/components';
import { ageTags, useCorePersonas, useStartNewActions } from '../lib';
import type { AudienceDraftCard } from '../lib/audienceDrafts';
import { StartNewMenu } from './StartNewMenu';
import { SuggestedAudienceCards } from './SuggestedAudienceCards';
import { useTranslation } from 'react-i18next';

interface CoreSegmentsProps {
  personaIds?: string[];
  /** Audiences still being built into personas, shown alongside the ones the report was given. */
  suggested?: AudienceDraftCard[];
}

export const CoreSegments = ({ personaIds, suggested = [] }: CoreSegmentsProps) => {
  const { t } = useTranslation('analysis');

  const navigate = useNavigate();
  const { analysisId } = useParams<{ analysisId: string }>();
  const { personas, isLoading, hasPersonas } = useCorePersonas(personaIds);

  // An audience the report suggested becomes a real persona once saved, but its id is recorded
  // on the suggestion rather than in the report's `personaIds`, so reading only the latter
  // would leave a saved audience untestable.
  const testablePersonaIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...(personas ?? []).map((persona) => persona.id),
          ...suggested.flatMap((card) => (card.savedPersonaId ? [card.savedPersonaId] : [])),
        ]),
      ),
    [personas, suggested],
  );

  const { onFocusGroup, onContentTest } = useStartNewActions(testablePersonaIds, analysisId);

  if (!isLoading && !hasPersonas && suggested.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-start justify-between gap-4">
        <AnalysisSubHeader
          title={t('analysisResult.coreSegments.coreSegments')}
          subtitle={t('analysisResult.coreSegments.selectASegmentToOpenItsFullProfile')}
          subtitleTone="meta"
        />
        {(onFocusGroup || onContentTest) && (
          <StartNewMenu onFocusGroup={onFocusGroup} onContentTest={onContentTest} />
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
          {personas?.map((persona) => (
            <PersonaCard
              key={persona.id}
              name={persona.personaTitle || persona.segmentName}
              age={persona.demographicsData?.data.age}
              title={persona.demographicsData?.data.profession}
              tags={ageTags(persona.demographicsData?.data.age)}
              description={<AnalysisMarkdown text={persona.personaBrief} />}
              photoAssetId={persona.photoAssetId}
              mouseOverAssetId={persona.mouseOverAssetId}
              onClick={
                analysisId
                  ? () => void navigate(ROUTES.IQ_AUDIENCE_PERSONA_DETAIL(analysisId, persona.id))
                  : undefined
              }
            />
          ))}
          <SuggestedAudienceCards
            cards={suggested}
            onOpen={
              analysisId
                ? (draftId) => void navigate(ROUTES.IQ_AUDIENCE_PERSONA_DETAIL(analysisId, draftId))
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
};
